import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReportsRopaProcess } from '../Process';
import { FILTER } from '../filter-types';
import styled from 'styled-components';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--vo-celeste)';
const GREEN = 'var(--vo-success)';
const DIVIDER = 'var(--vo-cloud)';

/* ── Page-view header card (isPageView=true) ── */
const HeaderCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 16px;
`;
const AreaLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .08em;
  margin: 0 0 4px;
`;
const ReportName = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${INK};
  margin: 0 0 8px;
`;
const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;
const ScopeBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.$color};
  background: ${p => p.$bg};
  padding: 3px 12px;
  border-radius: 20px;
  white-space: nowrap;
`;
const MetaText = styled.span`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
`;
const StatBadge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.$color};
  background: ${p => p.$bg};
  padding: 3px 12px;
  border-radius: 20px;
  white-space: nowrap;
`;
const ResponsibleLine = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
  margin: 10px 0 0;
  line-height: 1.55;
`;
const EmptyCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 48px 24px;
  text-align: center;
  color: ${MUTED};
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;

/* ── Accordion (used in Company view) ── */
const AccordionWrap = styled.div`
  border: 1px solid ${DIVIDER};
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 12px;
`;
const AccordionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: linear-gradient(135deg, var(--vo-capri-50), var(--vo-capri-50));
  cursor: pointer;
  &:hover { background: linear-gradient(135deg, var(--vo-capri-50), var(--vo-capri-100)); }
`;
const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
`;
const IconBox = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 9px;
  background: color-mix(in srgb, var(--vo-cobalto) 12%, transparent);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;
const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 3px;
`;
const AreaName = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${INK};
  margin: 0;
`;
const Badge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${p => p.$color};
  background: ${p => p.$bg};
  padding: 2px 9px;
  border-radius: 10px;
`;
const ResponsibleTxt = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  color: ${MUTED};
  margin: 0;
`;
const Chevron = styled.svg`
  flex-shrink: 0;
  transition: transform 0.2s;
  transform: ${p => p.$open ? 'rotate(180deg)' : 'rotate(0deg)'};
`;
const AccordionContent = styled.div`
  padding: 12px 16px;
  background: var(--vo-mist);
`;
const EmptyTxt = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
  text-align: center;
  padding: 16px 0;
  margin: 0;
`;

const formatDate = d => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt) ? d : dt.toLocaleDateString('pt-BR');
};

export const ReportsRopaArea = ({
  area,
  isPageView = false,
  ropaName,
  ropaScope,
  ropaDate,
  filter,
  process,
  data,
  category,
  legalBase,
  processing,
  position,
}) => {
  const { t } = useTranslation(['dashboard', 'mapping']);
  const [open, setOpen] = useState(false);

  const totalProcesses = area.processes?.length || 0;
  const totalData = area.processes?.reduce((acc, p) => acc + (p.dataMapping?.length || 0), 0) || 0;
  const totalTreatments = area.processes?.reduce((acc, p) =>
    acc + p.dataMapping?.reduce((da, dm) => da + (dm.dataProcessing?.length || 0), 0) || 0, 0) || 0;

  const filteredProcesses = area.processes?.filter(processFilter => {
    if (filter === FILTER.PROCESS && !!process)
      return processFilter.name === process;
    if (filter === FILTER.DATA && !!data)
      return processFilter.dataMapping.some(dm => dm.name === data);
    if (filter === FILTER.CATEGORY && !!category)
      return processFilter.dataMapping.some(dm => dm.category === category);
    if (filter === FILTER.PROCESSING && !!processing)
      return processFilter.dataMapping.some(dm =>
        dm.dataProcessing.some(dp => dp.processing === processing));
    if (filter === FILTER.LEGAL_BASE && !!legalBase)
      return processFilter.dataMapping.some(dm =>
        dm.dataProcessing.some(dp => dp.legalBase === legalBase));
    if (filter === FILTER.POSITION && !!position)
      return processFilter.dataMapping.some(dm =>
        dm.dataProcessing.some(dp => dp.position === position));
    return true;
  });

  if (isPageView) {
    return (
      <>
        <HeaderCard>
          <AreaLabel>{t('dashboard:ropa.show.areaReportLabel')}</AreaLabel>
          <ReportName>{ropaName}</ReportName>
          <MetaRow>
            <ScopeBadge $color={GREEN} $bg="var(--vo-success-10)">
              {t('dashboard:ropa.show.scopeLabel')}: {ropaScope}
            </ScopeBadge>
            <MetaText>{t('dashboard:ropa.table.generatedAt')} {formatDate(ropaDate)}</MetaText>
            <StatBadge $color={MUTED} $bg="var(--vo-mist)">
              {totalProcesses} {t('dashboard:ropa.show.statsProcesses')}
            </StatBadge>
            <StatBadge $color={BLUE} $bg="var(--vo-capri-50)">
              {totalData} {t('dashboard:ropa.show.statsData')}
            </StatBadge>
            <StatBadge $color={GREEN} $bg="var(--vo-success-10)">
              {totalTreatments} {t('dashboard:ropa.show.statsTreatments')}
            </StatBadge>
          </MetaRow>
          {(area.user || area.description) && (
            <ResponsibleLine>
              {area.user ? `${t('dashboard:ropa.show.responsible')}: ${area.user}` : ''}
              {area.user && area.description ? ' · ' : ''}
              {area.description ?? ''}
            </ResponsibleLine>
          )}
        </HeaderCard>
        {(!filteredProcesses || filteredProcesses.length === 0) ? (
          <EmptyCard>{t('mapping:ropa.area.noProcesses')}</EmptyCard>
        ) : (
          filteredProcesses.map((itemProcess, index) => (
            <ReportsRopaProcess
              key={itemProcess._id ?? index}
              process={itemProcess}
              filter={filter}
              data={data}
              category={category}
              legalBase={legalBase}
              processing={processing}
              position={position}
            />
          ))
        )}
      </>
    );
  }

  return (
    <AccordionWrap>
      <AccordionHeader onClick={() => setOpen(o => !o)}>
        <HeaderLeft>
          <IconBox>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--vo-cobalto)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          </IconBox>
          <div style={{ flex: 1, minWidth: 0 }}>
            <NameRow>
              <AreaName>{area.name}</AreaName>
              <Badge $color={MUTED} $bg="var(--vo-mist)">
                {totalProcesses} {t('mapping:ropa.area.processes')}
              </Badge>
              <Badge $color={BLUE} $bg="var(--vo-capri-50)">
                {totalData} {t('mapping:ropa.area.data')}
              </Badge>
              <Badge $color={GREEN} $bg="var(--vo-success-10)">
                {totalTreatments} {t('dashboard:ropa.show.statsTreatments')}
              </Badge>
            </NameRow>
            <ResponsibleTxt>
              {t('dashboard:ropa.show.responsible')}: {area.user || '—'}
              {area.description ? ` · ${area.description}` : ''}
            </ResponsibleTxt>
          </div>
        </HeaderLeft>
        <Chevron
          $open={open}
          width="16" height="16" viewBox="0 0 24 24"
          fill="none" stroke="var(--vo-cobalto)" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9"/>
        </Chevron>
      </AccordionHeader>

      {open && (
        <AccordionContent>
          {(!filteredProcesses || filteredProcesses.length === 0) ? (
            <EmptyTxt>{t('mapping:ropa.area.noProcesses')}</EmptyTxt>
          ) : (
            filteredProcesses.map((itemProcess, index) => (
              <ReportsRopaProcess
                key={itemProcess._id ?? index}
                process={itemProcess}
                filter={filter}
                data={data}
                category={category}
                legalBase={legalBase}
                processing={processing}
                position={position}
              />
            ))
          )}
        </AccordionContent>
      )}
    </AccordionWrap>
  );
};
